import FreeText from '@algbra/ui/components/FreeText/FreeText'
import { Column, Row } from '@algbra/ui/components/Grid/Grid'
import Heading from '@algbra/ui/components/Heading/Heading'
import Icon from '@algbra/ui/components/Icon/Icon'
import Permalink from '@algbra/ui/components/Permalink/Permalink'
import Whitespace, {
  WhitespaceProps,
} from '@algbra/ui/components/Whitespace/Whitespace'
import { getIconByName } from '@algbra/ui/icons'
import { resolveAsImage } from '@algbra/ui/resolvers'
import { GatsbyResolvedAsset } from '@algbra/ui/types/image'
import classNames from 'classnames'
import { GatsbyImage } from 'gatsby-plugin-image'
import React, { useEffect, useRef, useState } from 'react'
import styles from './ProductFeatureBlock.module.scss'
import axios from 'axios'

export type ZoneProductFeatureBlockProps = {
  feature: ZoneProductFeature
}
export type ZoneProductFeature = {
  id: string
  title: string
  body: string
  imageAlignment: string
  isAvailable: boolean
  whitespace: WhitespaceProps['type']
  image: GatsbyResolvedAsset
  detailLinkUrl?: string
  detailLinkTitle?: string
  detailLinkTarget?: string
  imageUniqueClassname: string
  finePrint?: string
  finePrintIcon?: GatsbyResolvedAsset
  logoImage?: GatsbyResolvedAsset
  newStyle?: boolean
  rates?: boolean
}
export default function ZoneProductFeatureBlock({
  feature,
}: ZoneProductFeatureBlockProps): JSX.Element {
  const videoRef = useRef<HTMLVideoElement | null>(null)
  const [topRates, setTopRates] = useState(null)

  const {
    id,
    title,
    body,
    imageAlignment,
    image,
    isAvailable,
    whitespace,
    detailLinkTitle,
    detailLinkUrl,
    detailLinkTarget,
    imageUniqueClassname,
    finePrint,
    finePrintIcon,
    logoImage,
    newStyle,
    rates = false,
  } = feature
  const isBlank = detailLinkTarget === 'new'

  useEffect(() => {
    if (rates) {
      axios
        .get('/.netlify/functions/topRates')
        .then(response => {
          const filteredRates = response.data.filter(
            item => item.durationType !== 'DAY'
          )
          setTopRates(filteredRates)
        })
        .catch(() => {
          setTopRates(null)
        })
    }
  }, [rates])

  useEffect(() => {
    if (!rates || !videoRef.current) return

    const video = videoRef.current

    video.muted = true
    video.defaultMuted = true
    video.setAttribute('muted', '')
    video.setAttribute('playsinline', '')
    video.setAttribute('webkit-playsinline', '')
    video.autoplay = true
    video.loop = true
    video.playsInline = true

    video.load()

    const playVideo = async () => {
      try {
        await video.play()
      } catch (err) {
        console.error('Autoplay failed:', err)
      }
    }

    const onCanPlay = () => {
      playVideo()
    }

    video.addEventListener('canplay', onCanPlay)

    playVideo()

    return () => {
      video.removeEventListener('canplay', onCanPlay)
    }
  }, [rates])

  return (
    <Whitespace
      type={whitespace}
      className={styles[`whiteSpace__${imageUniqueClassname}`]}
    >
      <Row
        className={classNames(
          styles.container,
          styles[`imageAlignment__${imageAlignment}`]
        )}
      >
        <Column auto className={styles.contentCol}>
          <div className={styles.content}>
            {!isAvailable && (
              <span className={styles.availableSoon}>Available Soon</span>
            )}
            {logoImage && (
              <GatsbyImage
                image={resolveAsImage(logoImage)}
                alt={title}
                loading="lazy"
                className={classNames(styles.logoImage, {
                  [styles.newStyle]: !!newStyle,
                })}
              />
            )}
            <Heading type="lg" className={styles.title}>
              {title}
            </Heading>
            <FreeText type="md" innerHTML={body} className={styles.text}>
              {body}
            </FreeText>
            {detailLinkTitle && (
              <Permalink
                to={detailLinkUrl}
                isExternal={isBlank}
                isBlank={isBlank}
                className={styles.detailLinkContainer}
              >
                <FreeText type="m" className={styles.detailLink}>
                  {detailLinkTitle}
                </FreeText>
                <div
                  className={classNames({
                    [styles.newIcon]: !!newStyle,
                  })}
                >
                  <Icon icon={getIconByName('arrow-right')} size={24} />
                </div>
              </Permalink>
            )}
            {finePrint && (
              <div className={styles.finePrintContainer}>
                {finePrintIcon && (
                  <div
                    className={classNames(styles.finePrintIcon, {
                      [styles.newFinePrintIcon]: !!newStyle,
                    })}
                    style={{
                      backgroundImage:
                        finePrintIcon &&
                        `url(${finePrintIcon.resolved.publicURL})`,
                    }}
                  ></div>
                )}

                <FreeText className={styles.finePrint}>{finePrint}</FreeText>
              </div>
            )}
          </div>
        </Column>
        <Column
          className={classNames(styles.imageCol, {
            [styles.imagePos]: !!rates,
          })}
        >
          <div
            className={classNames(
              styles.imagePlacement,
              styles[`imageUnique__${imageUniqueClassname}`],
              {
                [styles.newStyle]: !!newStyle,
              }
            )}
          >
            <div
              className={classNames(styles.imageWrapper, {
                [styles.imageWrapperWithRates]: !!rates,
              })}
            >
              {!rates && (
                <GatsbyImage
                  image={resolveAsImage(image)}
                  alt={title}
                  loading="lazy"
                  className={classNames(styles.image, {
                    [styles.newStyle]: !!newStyle,
                  })}
                />
              )}
              {!!rates && (
                <div className={styles.videoWrapper}>
                  <video
                    ref={videoRef}
                    className={styles.video}
                    autoPlay
                    muted
                    loop
                    playsInline
                    preload="auto"
                  >
                    <source src="/video/ethical-savings.mp4" type="video/mp4" />
                  </video>
                </div>
              )}

              {topRates?.length >= 3 && (
                <div className={styles.ratesOverlay}>
                  <div className={classNames(styles.rate, styles.rate1)}>
                    <span>
                      {topRates[0].rate.toFixed(2)}% EPR<sup>*</sup>
                    </span>
                  </div>

                  <div className={classNames(styles.rate, styles.rate2)}>
                    <span>
                      {topRates[1].rate.toFixed(2)}% EPR<sup>*</sup>
                    </span>
                  </div>

                  <div className={classNames(styles.rate, styles.rate3)}>
                    <span>
                      {topRates[2].rate.toFixed(2)}% EPR<sup>*</sup>
                    </span>
                  </div>
                </div>
              )}
            </div>
          </div>
        </Column>
      </Row>
    </Whitespace>
  )
}
